import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo
} from "react";
import { get, throttle } from "lodash";
import { RaceProgram } from "@tvg/ts-types/Race";
import { FilterCells, useTheme } from "@tvg/design-system";
import CurrentRaceDetails from "../CurrentRaceDetails";
import RunnerList from "../../RunnerList";
import { Props } from "./types";
import {
  ScrollablePicksContainer,
  RaceCardPicksContainer,
  LegsPicker,
  RaceCardWrapper,
  FPPContainer
} from "./styled-components";
import useSetInView from "../../../hooks/useSetInView";
import { formattedScrollableWagerType } from "../../../utils/raceUtils";
import { SelectAllContainer } from "../RunnerModifier/styled-components";
import SelectAll from "../RunnerModifier/SelectAll";
import MobilePicksNavigation from "./MobilePicksNavigation";
import FreePastPerformance from "../FreePastPerformance";

export const getFilterOptions = (races: RaceProgram[]) =>
  Array(races.length)
    .fill(null)
    .map((_, index) => ({ label: `Leg ${index + 1}`, value: `${index + 1}` }));

const ScrollablePicksRacecard = ({
  races,
  selectedBetType,
  selectedBetLeg,
  selections,
  onClickBetButton,
  isMobile,
  isBetSlipVisible,
  renderRunnerModifier,
  runnerModifier,
  runnerSortOrder,
  runnerSortBy,
  onSelectBetLeg,
  onClickActiveBetsButton,
  freePastPerformanceData
}: Props) => {
  const theme = useTheme();
  const [activeLeg, setActiveLeg] = useState<string | undefined>("1");
  const [contentHeight, setContentHeight] = useState("auto");
  const containerRef = useRef<HTMLDivElement>(null);
  const racesRefs = useRef<(HTMLDivElement | null)[]>([]);
  const { legInView, isScrolling } = useSetInView(
    racesRefs,
    containerRef,
    selectedBetLeg + 1,
    isMobile
  );

  const scrollableWagerType = useMemo(
    () => formattedScrollableWagerType(selectedBetType?.legCount),
    [selectedBetType]
  );

  const scrollIntoView = useCallback(
    (leg: string | number) => {
      const race = racesRefs.current[Number(leg) - 1 || 0];
      const container = containerRef.current;
      if (race && container) {
        const offsetAttr = isMobile ? "offsetLeft" : "offsetTop";
        const elementOffsetStart = get(race, offsetAttr, 0);
        const containerOffsetStart = get(container, offsetAttr, 0);
        const newPosition = elementOffsetStart - containerOffsetStart;
        container.scrollTo({ [isMobile ? "left" : "top"]: newPosition });
      }
    },
    [isMobile]
  );

  useEffect(() => {
    scrollIntoView(1);
    setContentHeight("auto");

    if (racesRefs.current.length > 0) {
      racesRefs.current = racesRefs.current.slice(0, selectedBetType?.legCount);
    }
  }, [selectedBetType?.type.id]);

  useEffect(() => {
    if (isMobile) {
      scrollIntoView(selectedBetLeg + 1);
    }
  }, [isMobile, selectedBetLeg]);

  useEffect(() => {
    setActiveLeg(`${legInView}`);

    if (!isScrolling && selectedBetLeg !== legInView - 1) {
      onSelectBetLeg(legInView - 1);
    }
  }, [legInView, isScrolling]);

  useEffect(() => {
    let resizeObserver: ResizeObserver;
    const currentLeg = racesRefs.current[Number(activeLeg) - 1 || 0];
    if (currentLeg && isMobile && !isScrolling) {
      resizeObserver = new ResizeObserver(
        throttle(() => {
          setContentHeight(`${currentLeg.offsetHeight + 12}px`);
        }, 300)
      );
      resizeObserver.observe(currentLeg);
    }
    return () => resizeObserver && resizeObserver.disconnect();
  }, [isMobile, activeLeg, isScrolling, racesRefs.current]);

  const renderSelectAll = useCallback(
    (race: RaceProgram, leg: number) => () =>
      (
        <SelectAllContainer data-qa-label="select-all-container">
          <SelectAll
            leg={leg}
            selections={selections}
            selectedBetType={scrollableWagerType}
            currentRace={race}
            isScrollableRacecard
          />
        </SelectAllContainer>
      ),
    [selections, scrollableWagerType]
  );

  return (
    <>
      {!isMobile && (
        <LegsPicker theme={theme}>
          <FilterCells
            isStretch
            activeValue={activeLeg}
            onChange={scrollIntoView}
            filterOptions={getFilterOptions(races)}
            size="m"
          />
        </LegsPicker>
      )}
      <ScrollablePicksContainer
        theme={theme}
        ref={containerRef}
        contentHeight={contentHeight}
      >
        {(races || []).map((race: RaceProgram, index: number) => (
          <RaceCardWrapper
            ref={(ref) => {
              racesRefs.current[index] = ref;
            }}
            key={`race-card-picks-container-${race.id}`}
          >
            <RaceCardPicksContainer data-qa-label="race-card-container">
              <CurrentRaceDetails
                leg={index + 1}
                isRacecardScrollable
                onClickActiveBetsButton={onClickActiveBetsButton}
                currentRace={race}
                renderSelectAll={
                  !isMobile ? renderSelectAll(race, index) : undefined
                }
              />
              {isMobile && renderRunnerModifier?.(race, true, index)}
              <RunnerList
                isScrollableRacecard
                scrollablePicksWagerType={scrollableWagerType}
                currentRace={race}
                legPosition={index}
                onClickBetButton={onClickBetButton}
                selections={selections}
                runnerModifier={runnerModifier}
                runnerSortOrder={runnerSortOrder}
                runnerSortBy={runnerSortBy}
                isBetSlipVisible={isBetSlipVisible}
                numRunnersAllRaces={races.map((r: RaceProgram) => ({
                  numRunners: r.numRunners
                }))}
              />
              {isMobile && (
                <MobilePicksNavigation
                  qaLabel={`mobile-picks-navigation-${index}`}
                  totalLegs={races.length}
                  currentLeg={index}
                  onSelectBetLeg={onSelectBetLeg}
                />
              )}
            </RaceCardPicksContainer>
            <FPPContainer>
              {freePastPerformanceData &&
                freePastPerformanceData.currentFPP && (
                  <FreePastPerformance
                    onDownload={freePastPerformanceData.onDownload}
                    title={freePastPerformanceData.title}
                    isMobile={freePastPerformanceData.isMobile}
                    fppSectionContent={
                      freePastPerformanceData.fppSectionContent
                    }
                    isLegView
                  />
                )}
            </FPPContainer>
          </RaceCardWrapper>
        ))}
      </ScrollablePicksContainer>
    </>
  );
};

export default ScrollablePicksRacecard;
